import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Gift, Clock, Users, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { membresiaService } from '../services/sorteoService'

const ActiveRaffles = () => {
  const navigate = useNavigate()
  const [raffles, setRaffles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  useEffect(() => {
    loadRaffles()
  }, [])
  
  const loadRaffles = async () => {
    try {
      setLoading(true)
      const sorteos = await membresiaService.getAllMembresias()
      // Filtrar solo sorteos activos y ordenar por fecha de creación (más reciente primero)
      const activeSorteos = sorteos
        .filter(sorteo => sorteo.status === 'active')
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      
      setRaffles(activeSorteos)
    } catch (err) {
      console.error('Error al cargar sorteos:', err)
      setError('Error al cargar los sorteos')
      // Fallback a datos estáticos en caso de error
      setRaffles([
        {
          id: 1,
          name: "iPhone 15 Pro Max",
          image_url: "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=300&h=200&fit=crop&crop=center",
          amount: 25000,
          total_boletas: 100000,
          boletas_vendidas: 45230,
          description: "El último iPhone con tecnología de punta",
          end_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString()
        }
      ])
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-CO').format(num)
  }

  const calculateProgress = (sold, total) => {
    return Math.round((sold / total) * 100)
  }

  const calculateTimeLeft = (endDate) => {
    if (!endDate) return 'Sin fecha límite'
    
    const now = new Date()
    const end = new Date(endDate)
    const diff = end - now
    
    if (diff <= 0) return 'Finalizado'
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    
    if (days > 0) {
      return `${days} días ${hours}h`
    } else {
      return `${hours}h`
    }
  }

  const handleBuyTickets = (raffleId) => {
    navigate(`/membresia/${raffleId}`)
  }

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Membresías Activas</h2>
        <p className="text-xl text-gray-600">Cargando membresías...</p>
          </div>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="py-20 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Membresías Activas</h2>
            <p className="text-xl text-red-600">{error}</p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="membresias-activas" className="py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Membresías <span className="gradient-text">Activas</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Participa en nuestras membresías activas y gana premios increíbles
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {raffles.map((raffle, index) => (
            <motion.div
              key={raffle.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="card-modern overflow-hidden group hover:scale-105 transition-transform duration-300"
            >
              <div className="relative">
                <img 
                  src={raffle.image_url || "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=300&h=200&fit=crop&crop=center"} 
                  alt={raffle.name}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-4 left-4 bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  Sorteo
                </div>
                <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {calculateTimeLeft(raffle.end_date)}
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold mb-3 text-white group-hover:text-primary-400 transition-colors">
                  {raffle.name}
                </h3>
                
                <div className="flex justify-between items-center mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary-400">${formatNumber(raffle.amount)} COP</div>
                    <div className="text-xs text-gray-400">premio</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-secondary-400">{formatNumber(raffle.boletas_vendidas || 0)}</div>
                    <div className="text-xs text-gray-400">vendidos</div>
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Progreso</span>
                    <span>{calculateProgress(raffle.boletas_vendidas || 0, raffle.total_boletas || 100000)}%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${calculateProgress(raffle.boletas_vendidas || 0, raffle.total_boletas || 100000)}%` }}
                    ></div>
                  </div>
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full btn-primary flex items-center justify-center gap-2"
                  onClick={() => navigate(`/membresia/${raffle.id}`)}
                >
                  <Gift className="w-4 h-4" />
                  Comprar Membresías
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {raffles.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mt-12"
          >
            <p className="text-xl text-gray-400">No hay membresías activas en este momento</p>
          </motion.div>
        )}

        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="mt-16 card-modern p-8 text-center"
        >
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="text-3xl font-bold text-white mb-2">100,000</div>
              <div className="text-gray-400">Números únicos por membresía</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-2">24/7</div>
              <div className="text-gray-400">Membresías disponibles</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-2">100%</div>
              <div className="text-gray-400">Transparente y seguro</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default ActiveRaffles